/* eslint-disable react/jsx-props-no-spreading */
import { useMemo } from 'react';
import { JobCardBaseProps, NavigateHandler } from '../../index';
import {
  CardLayout as Card,
  Header,
  Body,
  Footer,
  getHeaderProps,
  getFooterProps,
  type GetPropsParam,
} from '../Layout';
import { InfoList } from '../InfoList';

/**
 * JobCardNoPicProps インターフェース
 * JobCardBasePropsを拡張したプロパティ
 */
interface JobCardNoPicProps extends JobCardBaseProps {
  /**
   * T原稿として表示するかどうか
   * ※画像無し表示の場合は原稿種別に関わらずfalse
   */
  shouldDisplayAsCardTypeT?: boolean;
}

/**
 * JobCardNoPic コンポーネント
 * 画像無しの求人カード
 * @param {JobCardNoPicProps} props - コンポーネントのプロパティ
 * @returns {JSX.Element} JobCardNoPicのJSX要素
 */
export const JobCardNoPic = ({
  job,
  destinationURL,
  shouldDisplayAsCardTypeT = false,
  bookmarkUrlQuery,
  similarKeywordArea,
  ...handlers
}: JobCardNoPicProps) => {
  const {
    isApplicationWelcome,
    isApplied,
    isBookmarkSaved,
    jobContentOutline,
    jobContentDetail,
    targetMemberOutline,
    jobState,
    stations,
    salary,
    salaryExamples,
    businessContent,
  } = job;

  const {
    onNavigateTitle,
    onNavigateFooterButton,
    onApplyDirect,
    onApplyAgent,
    onBookmark,
    onNavigateToBookmarkList,
  } = handlers;

  /**
   * ヘッダーとフッターのプロパティを取得
   */
  const [headerProps, footerProps] = useMemo(() => {
    const arg: GetPropsParam = { job };
    return [getHeaderProps(arg), getFooterProps(arg)];
  }, [job]);

  /**
   * 求人詳細ページへのナビゲートハンドラーを生成
   * @param {NavigateHandler} handler - デフォルトのハンドラー
   * @returns {NavigateHandler} ナビゲートハンドラー
   */
  const handleNavigateToJobDetail =
    (handler?: NavigateHandler): NavigateHandler =>
    (event) => {
      event.currentTarget.href = destinationURL;
      if (handler) handler(event);
    };

  return (
    <Card
      isWelcome={isApplicationWelcome}
      isApplied={isApplied}
      isBookmarked={isBookmarkSaved}
    >
      <Header
        {...headerProps}
        onNavigateTitle={handleNavigateToJobDetail(onNavigateTitle)}
        onApplyDirect={onApplyDirect}
        onApplyAgent={onApplyAgent}
        similarKeywordArea={similarKeywordArea}
      />
      <Body>
        <InfoList
          detail={
            // T原稿 (かつ画像無表示でない) 場合のみ異なるデータを使用する
            shouldDisplayAsCardTypeT
              ? jobContentDetail || jobContentOutline
              : jobContentOutline
          }
          target={targetMemberOutline}
          location={jobState}
          station={stations}
          salary={{
            salary,
            example: salaryExamples,
          }}
          business={businessContent}
        />
      </Body>
      <Footer
        {...footerProps}
        bookmarkUrlQuery={bookmarkUrlQuery}
        onNavigateFooterButton={handleNavigateToJobDetail(
          onNavigateFooterButton
        )}
        onBookmark={onBookmark}
        onNavigateToBookmarkList={onNavigateToBookmarkList}
      />
    </Card>
  );
};
